import { useState } from 'react';
import { INDIAN_BANKS, bankAfterPick, bankFieldState } from '@siva/shared';
import { t } from '../i18n/index.js';

/**
 * Where somebody is paid: a list of the banks people here actually use, and "Other" for the rest.
 *
 * One component for both forms — the worker entering their own details and the admin correcting
 * them — because two copies of a dropdown drift, and the two would then disagree about which
 * banks exist.
 *
 * A name already on file that is not in the list keeps working: it selects Other and appears in
 * the text box, so opening an old record and saving it does not quietly change what it says. That
 * matters more than it sounds — the bank name is printed on a salary slip.
 */
export function BankNameField({
  value,
  onChange,
  required,
}: {
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
}) {
  /**
   * Set when they pick Other from the list. Needed as its own flag because an empty value cannot
   * tell us whether Other was chosen and nothing typed yet, or nothing has been chosen at all.
   */
  const [choseOther, setChoseOther] = useState(false);
  const { select, showOther } = bankFieldState(value, choseOther);

  const pick = (v: string) => {
    const next = bankAfterPick(value, v);
    setChoseOther(next.choseOther);
    if (next.value !== value) onChange(next.value);
  };

  return (
    <>
      <label>
        {t('bank_name')}
        <select
          value={select}
          onChange={(e) => pick(e.target.value)}
          required={required}
        >
          <option value="">{t('bank_choose')}</option>
          {INDIAN_BANKS.map((b) => (
            <option key={b} value={b}>
              {b}
            </option>
          ))}
          <option value="other">{t('bank_other')}</option>
        </select>
      </label>
      {showOther && (
        <label>
          {t('bank_other_name')}
          <input
            value={value}
            onChange={(e) => onChange(e.target.value)}
            required={required}
            maxLength={80}
            autoComplete="off"
            placeholder={t('bank_name_hint')}
          />
        </label>
      )}
    </>
  );
}
